/**
 * 連携用のプログラム (Claude・Codex 用の MCP) の版の判定 (mcp-stale-client-and-creditor-tools-20261015)。
 *
 * 職員 API は MCP からのリクエストで「本人が最後に使った版と日時」を記録する (`mcpClientVersion` /
 * `mcpClientSeenAt`)。この画面と同じ配信元から出している MCP の版 (`MCP_SERVER_VERSION`) より古ければ、
 * AIと連携の詳細とユーザー管理で履歴を確認できる。別端末・リモートコネクタも同じ履歴へ記録するため、
 * 現在の端末への導入や更新の要否は判定できない。ヘッダーの警告には使わない。
 *
 * 判定は BE・MCP 本体と同じ `backend/mcp/client-version.ts` を使う (版の比較・legacy の扱いを 1 か所に)。
 */
import {
  isMcpClientOutdated,
  MCP_LEGACY_CLIENT_VERSION,
  normalizeMcpClientVersion,
} from '../../../backend/mcp/client-version';
import { MCP_SERVER_VERSION } from '../../../backend/mcp/version';

/** 配信中の版 (この画面と一緒に配信している連携用のプログラムの版)。 */
export const CURRENT_MCP_VERSION = MCP_SERVER_VERSION;
/** 本人への案内を出す期間 (最後に使ってからの日数)。使わなくなった人に出し続けない。 */
export const MCP_UPDATE_NOTICE_WINDOW_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

export type McpClientStatus =
  /** 記録が無い (使ったことが無い / 0.8.0 対応の BE の前) */
  | { readonly kind: 'none' }
  | {
      readonly kind: 'current' | 'outdated';
      /** 記録された版 (x.y.z か legacy) */
      readonly version: string;
      /** 版を送らない古いプログラム (0.8.0 より前のブラウザ許可方式) */
      readonly legacy: boolean;
      readonly seenAt: string | null;
    };

/** 記録された版と日時から状態を決める。 */
export function mcpClientStatus(
  recordedVersion: unknown,
  seenAt: unknown,
  current: string = CURRENT_MCP_VERSION,
): McpClientStatus {
  const version = normalizeMcpClientVersion(recordedVersion);
  if (version === null) return { kind: 'none' };
  return {
    kind: isMcpClientOutdated(version, current) ? 'outdated' : 'current',
    version,
    legacy: version === MCP_LEGACY_CLIENT_VERSION,
    seenAt: typeof seenAt === 'string' && seenAt !== '' ? seenAt : null,
  };
}

/**
 * 本人に「更新が必要」を出すか。古い版を {@link MCP_UPDATE_NOTICE_WINDOW_DAYS} 日以内に使っていたときだけ。
 * 日時が読めないときは出す (古い版を使っていたことは確か)。
 */
export function shouldShowMcpUpdateNotice(status: McpClientStatus, nowMs: number): boolean {
  if (status.kind !== 'outdated') return false;
  if (status.seenAt === null) return true;
  const seen = Date.parse(status.seenAt);
  if (Number.isNaN(seen)) return true;
  return nowMs - seen <= MCP_UPDATE_NOTICE_WINDOW_DAYS * DAY_MS;
}
